import Deck from './Deck';

/**
 * GameStart class handles the procedure to determine which player starts dealing cards.
 * 
 * Rules:
 * - Any player cuts the deck and reveals a card
 * - Based on the suit of the revealed card, the starting dealer is determined:
 *   - Oros (Coins): Player to the right
 *   - Copas (Cups): Player opposite (across)
 *   - Espadas (Swords): Player to the left
 *   - Bastos (Clubs): The same player who revealed the card
 */
class GameStart {
    constructor(players) {
        if (!players || players.length !== 4) {
            throw new Error('GameStart requires exactly 4 players');
        }
        this.players = players;
        this.deck = new Deck();
        this.deck.shuffle();
    }

    /**
     * Simulates cutting the deck and revealing a card
     * @param {number} cuttingPlayerIndex - Index (0-3) of the player cutting the deck
     * @returns {Object} Result containing the revealed card and the starting dealer
     */
    cutDeck(cuttingPlayerIndex) {
        if (cuttingPlayerIndex < 0 || cuttingPlayerIndex >= 4) {
            throw new Error('Player index must be between 0 and 3');
        }

        // Simulate cutting the deck - get a random card
        const cutPosition = Math.floor(Math.random() * this.deck.remaining);
        const revealedCard = this.deck.cards[cutPosition];

        // Determine the starting dealer based on the suit
        const dealerIndex = this.getDealerIndex(cuttingPlayerIndex, revealedCard.suit);

        return {
            cuttingPlayer: this.players[cuttingPlayerIndex],
            revealedCard: revealedCard,
            startingDealer: this.players[dealerIndex],
            dealerIndex: dealerIndex
        };
    }

    /**
     * Determines the dealer index based on the suit of the revealed card
     * @param {number} cuttingPlayerIndex - Index of the player who cut the deck
     * @param {string} suit - Suit of the revealed card ('oros', 'copas', 'espadas', 'bastos')
     * @returns {number} Index of the starting dealer
     */
    getDealerIndex(cuttingPlayerIndex, suit) {
        switch (suit) {
            case 'oros': // Coins - player to the right
                return (cuttingPlayerIndex + 1) % 4;

            case 'copas': // Cups - player opposite (across)
                return (cuttingPlayerIndex + 2) % 4;

            case 'espadas': // Swords - player to the left
                return (cuttingPlayerIndex + 3) % 4; // Same as -1 in modulo 4

            case 'bastos': // Clubs - same player
                return cuttingPlayerIndex;

            default:
                throw new Error(`Unknown suit: ${suit}`);
        }
    }

    /**
     * Gets the position description relative to the cutting player
     * @param {number} cuttingPlayerIndex - Index of the player who cut the deck
     * @param {number} dealerIndex - Index of the starting dealer
     * @returns {string} Position description ('same', 'right', 'opposite', 'left')
     */
    getPositionDescription(cuttingPlayerIndex, dealerIndex) {
        const diff = (dealerIndex - cuttingPlayerIndex + 4) % 4;

        switch (diff) {
            case 0:
                return 'same';
            case 1:
                return 'right';
            case 2:
                return 'opposite';
            case 3:
                return 'left';
            default:
                return 'unknown';
        }
    }

    /**
     * Simulates the complete starting procedure with a random cutting player
     * @returns {Object} Result of the deck cutting procedure
     */
    determineStartingDealer() {
        // Randomly select a player to cut the deck
        const randomCuttingPlayer = Math.floor(Math.random() * 4);
        return this.cutDeck(randomCuttingPlayer);
    }
}

export default GameStart;
